import {Button, CustomSearch, DisplayItemsPerPageSelect, Paginator} from 'components';
import { AdminList } from '../AdminList';
import { StyledAdminEditPaymentHistory } from './styles';
import { DataExportButton } from '../DataExportButton';
import { RefreshButton } from '../RefreshButton';
import {RootState, useAppDispatch, useAppSelector} from 'store/store';
import {
  paymentsListSetPage,
  selectAdminPayments,
  setConfirmationStatusFilter,
  setCreatedAtDateFilter,
  setItemsPerPaymentsListPage,
  setSearch,
  setSort,
  setUpdatedAtDateFilter,
} from 'store/slices/adminPaymentSlice';
import { useSelector } from 'react-redux';
import { fetchExportPaymentHistory, fetchPaymentHistory } from 'store/thunks/adminPaymentThunks';
import { AdminPaymentHistoryListHeader } from '../AdminPaymentHistoryListHeader';
import { AdminPaymentHistoryListRow } from '../AdminPaymentHistoryListRow';
import { IAdminPayment, TSortPaymentHistoryByHeaderTitle } from 'types';
import { downloadCsvFile, getFormattedServerDateTimeRequest, getNextSortDir } from 'utils';
import {useEffect, useState} from 'react';
import {apiAdminRoutes} from '../../../../lib';

interface IAdminEditPaymentHistoryProps {
  children?: React.ReactNode;
}

export const AdminEditPaymentHistory = ({ children }: IAdminEditPaymentHistoryProps) => {
  const dispatch = useAppDispatch();
  const { payments } = useSelector(selectAdminPayments);

  const [editedPayments, setEditedPayments] = useState<IAdminPayment[]>(payments);
  const [hasLocalEdit, setHasLocalEdit] = useState(false);
  const { token } = useAppSelector((state) => state.auth);

  const { pagesQuantity, currentPage, itemsPerPage, sortBy, sortDir } = useSelector(
    (state: RootState) => state.adminPayments,
  );
  useEffect(() => {
    if (!hasLocalEdit) {
      setEditedPayments(payments);
    }
  }, [payments]);
  const handlePaymentChange = (updatedPayment: IAdminPayment) => {
    setEditedPayments(prev =>
        prev.map(p => p.id === updatedPayment.id ? updatedPayment : p)
    );
    setHasLocalEdit(true);
  };

  const setSortHandler = (sortBy: {
    sortBy: keyof IAdminPayment;
    sortDir: 'ASC' | 'DESC' | 'NONE';
  }) => {
    dispatch(
      setSort({
        sortBy: sortBy.sortDir === 'NONE' ? null : sortBy.sortBy,
        sortDir: sortBy.sortDir === 'NONE' ? null : sortBy.sortDir,
      }),
    );
    dispatch(fetchPaymentHistory());
  };

  const sortHandler = (nextSortBy: TSortPaymentHistoryByHeaderTitle) => {
    const nextSortDir = getNextSortDir(sortBy, nextSortBy, sortDir as 'ASC' | 'DESC' | 'NONE');
    setSortHandler({ sortBy: nextSortBy, sortDir: nextSortDir });
  };

  const setSearchFilterHandler = (value: string) => {
    dispatch(setSearch(value === '' ? null : value));
    dispatch(fetchPaymentHistory());
  };

  const filterByDateHandler = (filterByDate: {
    filterBy: keyof IAdminPayment;
    fromDate: Date;
    toDate: Date;
  }) => {
    const filterFromDate = getFormattedServerDateTimeRequest(String(filterByDate.fromDate));
    const filterToDate = getFormattedServerDateTimeRequest(String(filterByDate.toDate));

    switch (filterByDate.filterBy) {
      case 'createdAt':
        dispatch(
          setCreatedAtDateFilter({
            fromDate: filterFromDate === filterToDate ? null : filterFromDate,
            toDate: filterFromDate === filterToDate ? null : filterToDate,
          }),
        );
        break;
      case 'updatedAt':
        dispatch(
          setUpdatedAtDateFilter({
            fromDate: filterFromDate === filterToDate ? null : filterFromDate,
            toDate: filterFromDate === filterToDate ? null : filterToDate,
          }),
        );
        break;
      default:
        return;
    }

    dispatch(fetchPaymentHistory());
  };

  const filterByValueHandler = (filterValue: string) => {
    dispatch(setConfirmationStatusFilter(filterValue === '-' ? null : filterValue));
    dispatch(fetchPaymentHistory());
  };

  const refreshButtonHandler = () => {
    dispatch(fetchPaymentHistory());
  };

  const handleSave = async () => {
    try {
      const changedPayments = editedPayments.filter((edited) => {
        const original = payments.find(p => p.id === edited.id);
        return original && original.status !== edited.status;
      });

      if (changedPayments.length === 0) return;

      await fetch(apiAdminRoutes.apiAdminUpdatePayment(), {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`, // ← вот это
        },
        body: JSON.stringify({
          payments: changedPayments.map(p => ({
            transactionId: p.hashId,
            status: p.status,
          })),
        }),
      });

      setHasLocalEdit(false);
      dispatch(fetchPaymentHistory());
    } catch (e) {
      console.error('save error', e);
    }
  };
  const dataExportButtonHandler = async () => {
    const data = await dispatch(fetchExportPaymentHistory()).unwrap();
    downloadCsvFile(data.exportData, 'Payment history');
  };

  const changeCurrentPageHandler = (currentPage: number) => {
    dispatch(paymentsListSetPage(currentPage));
    dispatch(fetchPaymentHistory());
  };

  const changeItemsPerPageQuantityHandler = (itemsQuantity: number) => {
    dispatch(setItemsPerPaymentsListPage(itemsQuantity));
    dispatch(fetchPaymentHistory());
  };

  return (
    <StyledAdminEditPaymentHistory>
      <AdminList
        renderControls={() => (
          <div className="admin-edit-history-controls">
            <CustomSearch onChange={setSearchFilterHandler} />
            <DataExportButton onClick={dataExportButtonHandler} />
            {hasLocalEdit && (
                <Button title="Save" onClick={handleSave} />
            )}
            <div className="admin-edit-history-controls__right">
              <RefreshButton onClick={refreshButtonHandler} />
            </div>
          </div>
        )}
      >
        <div className="admin-edit-history-list-body">
          <AdminPaymentHistoryListHeader
            onSort={sortHandler}
            onFilterByDate={filterByDateHandler}
            onFilterByValue={filterByValueHandler}
            sortBy={sortBy}
            sortDir={sortDir as 'ASC' | 'DESC' | 'NONE'}
          />
          {editedPayments.map((payment) => (
              <AdminPaymentHistoryListRow
                  key={payment.id}
                  payment={payment}
                  onChange={handlePaymentChange}
              />
          ))}
        </div>
      </AdminList>
      {pagesQuantity > 0 && (
        <div className="list-body-pagination">
          <Paginator
            onChange={changeCurrentPageHandler}
            pageQuantity={pagesQuantity}
            currentPage={currentPage}
          />
          <DisplayItemsPerPageSelect
            onChange={changeItemsPerPageQuantityHandler}
            currentItemsPerPageQuantity={itemsPerPage}
            itemsPerPageRates={[10, 15, 25, 50]}
          />
        </div>
      )}
    </StyledAdminEditPaymentHistory>
  );
};
